import React from 'react';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import Dropzone from 'react-dropzone';

import {
    DocUploadViewModel,
    ErrorId,
    VerificationService,
    FieldValidationErrors,
    DocUploadResponse,
    RejectionReasons,
    logger,
    addFiles,
    removeFile,
    removeAllFiles,
    validateFieldById,
    FormFooterComponent as FormFooter,
    VerificationStepsEnum,
    UploadInfoComponent as UploadInfo,
    PoweredByComponent,
} from '@sheerid/jslib';

import { AcceptableUploadsComponent as AcceptableUploads } from '../AcceptableUploadsComponent';

interface StepDocUploadProps {
    verificationService: VerificationService;
    intl: any;
}

interface ImagePreview {
    name: string;
    url: string;
}

const StepDocUpload = ({ verificationService, intl }: StepDocUploadProps) => {
    const verificationResponse: DocUploadResponse = verificationService.verificationResponse as DocUploadResponse;
    const rejectionReasons: RejectionReasons[] = verificationResponse.rejectionReasons;
    const viewModel: DocUploadViewModel = verificationService.viewModel as DocUploadViewModel;
    const fieldValidationErrors: FieldValidationErrors = verificationService.fieldValidationErrors;
    const MAX_DOC_UPLOAD_DOCS_ALLOWED = 3;

    const getFileForView = (file: File) => ({
        name: file.name,
        url: file.type.includes('image') ? URL.createObjectURL(file) : '',
    });

    const currentFileNames: ImagePreview[] = [];
    if (viewModel.file1 && viewModel.file1.name) {
        currentFileNames.push(getFileForView(viewModel.file1));
    }
    if (viewModel.file2 && viewModel.file2.name) {
        currentFileNames.push(getFileForView(viewModel.file2));
    }
    if (viewModel.file3 && viewModel.file3.name) {
        currentFileNames.push(getFileForView(viewModel.file3));
    }

    const addFilesAndUpdate = (files: File[]) => {
        verificationService.updateViewModel(addFiles(viewModel, files));
    };

    const removeFileAndUpdate = (arrayIndex: number) => {
        const fileNumber = arrayIndex + 1;
        verificationService.updateViewModel(removeFile(viewModel, fileNumber));
    };

    const removeFilesAndUpdate = () => {
        logger.info('StepDocUploadComponent canceling upload');
        verificationService.updateViewModel(removeAllFiles(viewModel));
    };

    const updateFieldValidationErrorsAndAddFiles = (files: File[]) => {
        const acceptedFiles: File[] = [];
        const errors: ErrorId[] = [];

        for (const file of files) {
            const fileValidationError: ErrorId = validateFieldById(VerificationStepsEnum.docUpload, file) as ErrorId;

            if (fileValidationError === undefined) {
                acceptedFiles.push(file);
            } else {
                errors.push(fileValidationError);
            }
        }

        let nextFieldValidationErrors = undefined;
        if (errors) {
            nextFieldValidationErrors = produce(fieldValidationErrors, (draft) => {
                draft.docUpload = errors[0]; // we only care about one error at a time
            });
        }

        verificationService.updateFieldValidationErrors(nextFieldValidationErrors);
        addFilesAndUpdate(acceptedFiles);
    };

    const submitForm = () => {
        logger.info('StepDocUploadComponent submitting form');
        verificationService.submitStep(VerificationStepsEnum.docUpload, viewModel, verificationService.verificationResponse);
    };

    const onFileDrop = (draggedFiles: File[]) => {
        if (draggedFiles.length > 0) {
            updateFieldValidationErrorsAndAddFiles(draggedFiles);
        } else if (draggedFiles.length <= 0) {
            logger.error('No files were accepted or rejected. Unknown error.');
        }
    };

    const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFileDrop(Array.from(e.target.files));
    };

    return (
        <div id="sid-step-doc-upload" className="sid-l-container">
            <div className="sid-header sid-l-horz-center sid-l-space-top-md">
                <div className="sid-l-horz-center">
                </div>
                <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                    <FormattedHTMLMessage id="step.docUpload.title" defaultMessage="We need more information" />
                </div>
                {
                    rejectionReasons && rejectionReasons.length > 0 ?
                        <div className="sid-header__subtitle sid-l-horz-center sid-l-position">
                            <div>
                                <FormattedMessage
                                    id="step.docUpload.rejectedSubtitle"
                                    defaultMessage="We were unable to confirm your eligibility due to:"
                                />
                            </div>
                            <ul className="sid-upload-rejection-list">
                                {
                                    rejectionReasons.map((rejectionReason, index) =>
                                        <li key={index} className="sid-upload-rejection-list__li">
                                            <FormattedMessage
                                                id={`step.docUpload.rejectionReasons.${rejectionReason}`}
                                                defaultMessage={`${rejectionReason}`}
                                            />
                                        </li>,
                                    )
                                }
                            </ul>
                        </div>
                    :
                        <div className="sid-header__subtitle sid-l-horz-center sid-l-position sid-doc-upload-title">
                            <div>
                                <FormattedMessage
                                    id="step.docUpload.subtitle"
                                    defaultMessage=
                                        "Documents will be reviewed in a few days by staff at SheerID, a trusted partner."
                                />
                                &nbsp;
                            </div>
                        </div>
                }
            </div>

            {
                currentFileNames.length === 0 ?
                (
                    <div>
                        <div className="sid-upload-info sid-l-space-top-md">
                            <div className="sid-l-space-top-sm">
                                <UploadInfo verificationService={verificationService} />
                            </div>
                            <div className="sid-l-space-top-md">
                                <AcceptableUploads verificationService={verificationService} />
                            </div>
                        </div>
                        <div className="sid-upload-wrap sid-l-space-top-sm">
                            <div className="sid-dropzone-wrap">
                                <Dropzone onDrop={onFileDrop}>
                                    {({ getRootProps, getInputProps }) => (
                                    <section {...getRootProps()} className="sid-dropzone-wrap__dropzone">
                                        <input {...getInputProps()} />
                                        <p className="sid-dropzone-wrap__instructions sid-l-horz-center">
                                        <FormattedHTMLMessage
                                            id="step.docUpload.fileInstructions"
                                            defaultMessage='Drag &amp; drop your files here or <span class="sid-dropzone-wrap__link">browse</span>'
                                        />
                                        </p>
                                    </section>
                                    )}
                                </Dropzone>

                                <div className="sid-dropzone-wrap__accepted-types sid-l-space-top-sm">
                                    <FormattedHTMLMessage id="step.docUpload.acceptedTypes"
                                        defaultMessage="Acceptable file types: png, gif, jpg, bmp, pdf" />
                                </div>

                                {
                                    fieldValidationErrors.docUpload ? (
                                        <div className="sid-field-error sid-l-horz-center">
                                            <FormattedHTMLMessage
                                                id={`errorId.${fieldValidationErrors.docUpload}`}
                                                defaultMessage="No valid files"
                                            />
                                        </div>
                                    ) : null
                                }
                            </div>
                        </div>
                    </div>
                )
                :
                null
            }

            <div className="sid-upload-wrap sid-l-space-top-sm">
                {
                    currentFileNames.length > 0 ?
                    (
                        <div className="sid-file-list">
                            {
                                currentFileNames.map((file, arrayIndex) => (
                                    <div className="sid-file-list__file" key={file.name}>
                                        {
                                            file.url ?
                                                <img className="sid-file-list__thumbnail" src={file.url} alt="File thumbnail" />
                                            :
                                            <div className="sid-file-list__no-thumbnail">
                                                <img
                                                    alt="File thumbnail"
                                                    src="https://assets-resources.sheerid.com/common/images/2018/icons/default-image.svg"
                                                />
                                            </div>
                                        }
                                        <div className="sid-file-list__name">{file.name}</div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            className="sid-file-list__remove-btn"
                                            onClick={() => removeFileAndUpdate(arrayIndex)}
                                            onKeyPress={() => removeFileAndUpdate(arrayIndex)}
                                        />
                                    </div>
                                ))
                            }
                            {
                                currentFileNames.length < MAX_DOC_UPLOAD_DOCS_ALLOWED ?
                                (
                                    <div className="sid-add-more-files">
                                        <input
                                            type="file"
                                            id="file"
                                            className="sid-add-more-files__input"
                                            onChange={fileHandler}
                                            accept="image/jpeg,image/gif,image/png,application/pdf,image/bmp,image/gif"
                                        />
                                        <label
                                            className="sid-add-more-files__label sid-add-more-files--selectable"
                                            htmlFor="file"
                                        >
                                            <img
                                                className="sid-add-more-files__image"
                                                alt="add more files"
                                                src="https://assets-resources.sheerid.com/common/images/2018/icons/add-file.svg"
                                            />
                                            <FormattedMessage id="step.docUpload.addFile" defaultMessage="Add file" />
                                        </label>
                                    </div>
                                )
                                :
                                null
                            }
                             <div className="sid-submit sid-l-space-top-md">
                                <button
                                    id="sid-submit-doc-upload"
                                    onClick={submitForm}
                                    type="submit"
                                    className="sid-btn sid-btn--dark sid-submit__continue"
                                    aria-label="submit"
                                    disabled={(currentFileNames.length <= 0) ? true : false}
                                >
                                    <FormattedHTMLMessage id="step.docUpload.submitButtonLabel" defaultMessage="Continue" />
                                </button>
                                <button
                                    onClick={removeFilesAndUpdate}
                                    type="button"
                                    className="sid-btn sid-btn--light sid-submit__cancel sid-l-space-left-sm"
                                    aria-label="cancel"
                                    disabled={(currentFileNames.length <= 0) ? true : false}
                                >
                                    <FormattedHTMLMessage id="step.docUpload.cancelButtonLabel" defaultMessage="Cancel" />
                                </button>
                            </div>
                        </div>
                    )
                    :
                    null
                }
            </div>
            <FormFooter verificationService={verificationService}>
                <PoweredByComponent verificationService={verificationService} />
            </FormFooter>
        </div>
    );
};

export const StepDocUploadComponent = injectIntl(StepDocUpload);
